import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Trophy, Sparkles } from 'lucide-react'
import { rankForLevel } from '../lib/ranks'

export interface SectionClearData {
  sectionTitle: string
  questCount: number
  totalStars: number
  maxStars: number
  xpGain: number
  accent: string
  level?: number
}

interface Props {
  data: SectionClearData | null
  fx: boolean
  onClose: () => void
}

// Màn "hạ trùm" khi hoàn thành cả phần: cúp lớn + bảng tổng kết + huy hiệu + cấp bậc.
const SectionClearModal: React.FC<Props> = ({ data, fx, onClose }) => {
  if (!data) return null
  const rank = data.level ? rankForLevel(data.level) : null
  return (
    <AnimatePresence>
      {data && (
        <motion.div
          className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-md rounded-3xl bg-gradient-to-b from-white to-amber-50 shadow-2xl overflow-hidden text-center"
            initial={{ scale: fx ? 0.6 : 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={fx ? { type: 'spring', stiffness: 260, damping: 20 } : { duration: 0.15 }}
          >
            <div className="px-7 pt-7 pb-7">
              {/* Cúp xoay nhẹ */}
              <motion.div
                className="mx-auto w-24 h-24 rounded-full flex items-center justify-center shadow-lg"
                style={{ background: `radial-gradient(circle at 30% 30%, #fde68a, ${data.accent})` }}
                initial={{ rotate: fx ? -12 : 0, scale: 0.8 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={fx ? { type: 'spring', stiffness: 200, damping: 10 } : { duration: 0.15 }}
              >
                <Trophy size={52} className="text-white drop-shadow" />
              </motion.div>

              <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-amber-600">
                <Sparkles size={14} /> Chinh phục phần
              </div>
              <h3 className="mt-1 text-xl font-black text-gray-900">{data.sectionTitle}</h3>

              {/* Bảng tổng kết */}
              <div className="mt-5 grid grid-cols-3 gap-2">
                <div className="rounded-2xl bg-white border border-amber-100 py-3">
                  <div className="text-2xl font-black text-gray-900">{data.questCount}</div>
                  <div className="text-[11px] font-bold text-gray-400">ải đã qua</div>
                </div>
                <div className="rounded-2xl bg-white border border-amber-100 py-3">
                  <div className="flex items-center justify-center gap-0.5 text-amber-400">
                    <Star size={18} className="fill-amber-400" />
                    <span className="text-2xl font-black text-gray-900">{data.totalStars}</span>
                    <span className="text-xs font-bold text-gray-400 self-end mb-1">/{data.maxStars}</span>
                  </div>
                  <div className="text-[11px] font-bold text-gray-400">sao</div>
                </div>
                <div className="rounded-2xl bg-white border border-amber-100 py-3">
                  <div className="text-2xl font-black text-violet-600">+{data.xpGain}</div>
                  <div className="text-[11px] font-bold text-gray-400">XP</div>
                </div>
              </div>

              {/* Huy hiệu phần */}
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 font-bold text-sm">
                🏅 Huy hiệu: Chinh phục {data.sectionTitle}
              </div>

              {/* Cấp bậc hiện tại */}
              {rank && (
                <div className="mt-3 flex items-center justify-center gap-1.5 text-sm font-bold" style={{ color: rank.color }}>
                  <span className="text-base">{rank.icon}</span> {rank.name} · Cấp {data.level}
                </div>
              )}

              <button
                onClick={onClose}
                className="mt-6 w-full px-5 py-3 rounded-xl text-white font-black shadow-md hover:brightness-110"
                style={{ background: data.accent }}
              >
                Tuyệt vời! Tiếp tục 🎉
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SectionClearModal
